import { useDroppable } from "@dnd-kit/core";
import { Trophy } from "lucide-react";
import type { BoardLead, PipelineStage } from "@school-sales-ai/shared";
import { stageColorClasses } from "@/lib/stage-colors";
import { KanbanCard } from "./KanbanCard";

type Props = {
  stage: PipelineStage;
  leads: BoardLead[];
  activeId: string | null;
  /**
   * When set (swimlane mode), the droppable id is scoped to the lane so each
   * lane's columns are distinct drop targets. Omitted in the flat board.
   */
  laneKey?: string;
  /**
   * The column's true total from the server. `leads` is only the loaded page, so
   * the badge must never count it.
   */
  count: number;
  /**
   * Load the next page. Omitted when there is nothing more to load, when the
   * column has hit the server's cap, and in swimlane mode (not paginated).
   */
  onLoadMore?: () => void;
  /** How many more cards `onLoadMore` brings in — shown on the button. */
  loadMoreCount?: number;
  /** Jump to the table, filtered to this column — the exhaustive way in. */
  onSeeAll?: () => void;
};

export function KanbanColumn({
  stage,
  leads,
  activeId,
  laneKey,
  count,
  onLoadMore,
  loadMoreCount,
  onSeeAll,
}: Props): React.JSX.Element {
  const { setNodeRef, isOver } = useDroppable({
    id: laneKey ? `${laneKey}::${stage.key}` : stage.key,
  });
  const badgeClasses = stageColorClasses(stage.key, stage.color);
  const hasMore = leads.length < count;
  // The column's reporting role, made visible. Nothing on this board used to say
  // which column the reports actually measure, so renaming one silently changed
  // what every number meant.
  const isConversion = stage.stageRole === "conversion";
  const isLost = stage.stageRole === "lost";

  return (
    <div className="flex w-64 flex-shrink-0 flex-col gap-2 min-h-0 h-full">
      {/* Column header */}
      <div className="flex items-center justify-between px-1">
        <span
          className={[
            "flex items-center gap-1 text-xs font-semibold uppercase tracking-wide",
            isConversion ? "text-moss" : isLost ? "text-ink-400" : "text-ink-600",
          ].join(" ")}
        >
          {isConversion && (
            <Trophy className="h-3.5 w-3.5" aria-label="Columna de cierre ganado" />
          )}
          {stage.label}
        </span>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badgeClasses}`}
        >
          {count}
        </span>
      </div>

      {/* Drop zone */}
      <div
        ref={setNodeRef}
        className={[
          "flex flex-1 flex-col gap-2 rounded-xl p-2 transition-colors",
          "min-h-[120px] overflow-y-auto",
          isOver
            ? "bg-lime-pale outline-dashed outline-2 outline-moss"
            : isConversion
              ? "bg-ink-050 ring-1 ring-moss/30"
              : "bg-ink-050",
        ].join(" ")}
      >
        {leads.map((lead) => (
          <KanbanCard
            key={lead.id}
            lead={lead}
            isDragging={activeId === lead.id}
          />
        ))}

        {leads.length === 0 && (
          <div className="flex flex-1 items-center justify-center py-6">
            <span className="text-xs text-ink-200">Sin prospectos</span>
          </div>
        )}

        {hasMore && (
          <div className="flex flex-col items-center gap-1.5 py-2">
            <span className="text-[10px] text-ink-300">
              {leads.length} de {count}
            </span>
            {/* An explicit click, never a scroll sentinel: an auto-loading
                sentinel re-triggers itself as the column grows under it, and ran
                away to thousands of requests until the API rate-limited us. */}
            {onLoadMore && (
              <button
                type="button"
                onClick={onLoadMore}
                className="rounded-md border border-ink-200 bg-white px-2 py-1 text-[11px] font-semibold text-ink-700 hover:bg-ink-050 cursor-pointer"
              >
                Ver {loadMoreCount} más
              </button>
            )}
            {onSeeAll && (
              <button
                type="button"
                onClick={onSeeAll}
                className="text-[10px] text-ink-400 hover:text-moss hover:underline cursor-pointer"
              >
                Ver todos en Tabla →
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
